import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Chip, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';

function Registrations() {
  const [open, setOpen] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [formData, setFormData] = useState({
    registrationNo: '',
    enquiryNo: '',
    name: '',
    dateOfRegistration: new Date(),
    dateOfBirth: null,
    gender: '',
    address: '',
    email: '',
    mobile: '',
    qualification: '',
    courseName: '',
    courseDuration: '',
    courseFees: '',
    status: 'Active'
  });

  const qualifications = ['High School', 'Bachelor\'s', 'Master\'s', 'PhD', 'Other'];
  const genders = ['Male', 'Female', 'Other'];
  const courses = [
    { name: 'Full Stack Development', duration: '6 months', fees: 50000 },
    { name: 'Data Science', duration: '4 months', fees: 45000 },
    { name: 'Cloud Computing', duration: '3 months', fees: 40000 },
    { name: 'Cybersecurity', duration: '5 months', fees: 55000 }
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleDateChange = (field, date) => {
    setFormData({
      ...formData,
      [field]: date
    });
  };

  const handleCourseChange = (e) => {
    const selectedCourse = courses.find(course => course.name === e.target.value);
    setFormData({
      ...formData,
      courseName: selectedCourse.name,
      courseDuration: selectedCourse.duration,
      courseFees: selectedCourse.fees
    });
  };

  const generateRegistrationNo = () => {
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const count = (registrations.length + 1).toString().padStart(3, '0');
    return `REG${year}${count}`;
  };

  const handleSubmit = async () => {
    try {
      const registrationData = {
        ...formData,
        registrationNo: generateRegistrationNo(),
        enquiryNo: `ENQ${Date.now().toString().slice(-6)}`,
        dateOfRegistration: new Date()
      };

      const response = await fetch('http://localhost:5000/api/registrations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData),
      });

      if (response.ok) {
        const newRegistration = await response.json();
        setRegistrations([...registrations, newRegistration]);
        handleClose();
      }
    } catch (error) {
      console.error('Error submitting registration:', error);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({
      registrationNo: '',
      enquiryNo: '',
      name: '',
      dateOfRegistration: new Date(),
      dateOfBirth: null,
      gender: '',
      address: '',
      email: '',
      mobile: '',
      qualification: '',
      courseName: '',
      courseDuration: '',
      courseFees: '',
      status: 'Active'
    });
  };

  useEffect(() => {
    const fetchRegistrations = async () => {
      try {
        const response = await fetch('http://localhost:5000/api/registrations');
        if (response.ok) {
          const data = await response.json();
          setRegistrations(data);
        }
      } catch (error) {
        console.error('Error fetching registrations:', error);
      }
    };

    fetchRegistrations();
  }, []);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">Student Registrations</Typography>
        <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
          New Registration
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Registration No</TableCell>
              <TableCell>Enquiry No</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Date of Registration</TableCell>
              <TableCell>Course</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Fees</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {registrations.map((registration) => (
              <TableRow key={registration._id}>
                <TableCell>{registration.registrationNo}</TableCell>
                <TableCell>{registration.enquiryNo}</TableCell>
                <TableCell>{registration.name}</TableCell>
                <TableCell>{new Date(registration.dateOfRegistration).toLocaleDateString()}</TableCell>
                <TableCell>{registration.courseName}</TableCell>
                <TableCell>{registration.courseDuration}</TableCell>
                <TableCell>₹{registration.courseFees}</TableCell>
                <TableCell>
                  <Chip
                    label={registration.status}
                    color={registration.status === 'Active' ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>New Registration</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              name="name"
              fullWidth
              value={formData.name}
              onChange={handleInputChange}
              required
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date of Birth"
                value={formData.dateOfBirth}
                onChange={(date) => handleDateChange('dateOfBirth', date)}
                renderInput={(params) => <TextField {...params} fullWidth required />}
              />
            </LocalizationProvider>
            <FormControl fullWidth>
              <InputLabel>Gender</InputLabel>
              <Select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                required
              >
                {genders.map((gender) => (
                  <MenuItem key={gender} value={gender}>{gender}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Address"
              name="address"
              multiline
              rows={3}
              fullWidth
              value={formData.address}
              onChange={handleInputChange}
              required
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              fullWidth
              value={formData.email}
              onChange={handleInputChange}
              required
            />
            <TextField
              label="Mobile"
              name="mobile"
              fullWidth
              value={formData.mobile}
              onChange={handleInputChange}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Qualification</InputLabel>
              <Select
                name="qualification"
                value={formData.qualification}
                onChange={handleInputChange}
                required
              >
                {qualifications.map((qual) => (
                  <MenuItem key={qual} value={qual}>{qual}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Course</InputLabel>
              <Select
                name="courseName"
                value={formData.courseName}
                onChange={handleCourseChange}
                required
              >
                {courses.map((course) => (
                  <MenuItem key={course.name} value={course.name}>
                    {course.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Course Duration"
              value={formData.courseDuration}
              InputProps={{ readOnly: true }}
              fullWidth
            />
            <TextField
              label="Course Fees"
              value={formData.courseFees ? `₹${formData.courseFees}` : ''}
              InputProps={{ readOnly: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">Submit</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Registrations;